import { ModalsProvider } from '@mantine/modals';
import { Notifications, notifications } from '@mantine/notifications';
import { usePrevious } from '@mantine/hooks';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, httpLink, isNonJsonSerializable, splitLink } from '@trpc/client';
import { lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
    useLocation,
} from 'react-router-dom';
import superjson from 'superjson';
import { AppLayout } from './components/AppLayout/AppLayout.js';
import { Loader } from './components/Loader/Loader.js';
import { useClientAuth } from './auth/index.js';
import { GamesPage } from './pages/games/GamesPage.js';
import { ConfigsPage } from './pages/configs/ConfigsPage.js';
import { LoginPage } from './pages/login/LoginPage.js';
import { StudiosPage } from './pages/matrix/StudiosPage.js';
import type { LoginRedirectState } from './pages/login/loginRedirect.js';
import { GameAssetsRedirect, StudioRedirect } from './pages/redirects.js';
import {
    AUDIT_ROUTE,
    CONFIG_EDIT_PATH,
    CONFIGS_ROUTE,
    ENVIRONMENTS_ROUTE,
    GAME_ASSETS_REDIRECT_PATH,
    GAME_EDIT_PATH,
    GAMES_ROUTE,
    LOGIN_ROUTE,
    NEW_CONFIG_PATH,
    NEW_GAME_PATH,
    NEW_STUDIO_PATH,
    STUDIO_EDIT_PATH,
    STUDIO_REDIRECT_PATH,
    STUDIOS_ROUTE,
    USERS_ROUTE,
} from './domain/routes.js';
import { TRPC_PATH } from './config/api.js';
import { TRPCProvider } from './utils/trpc.js';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import { showStaleBuildNotice } from './components/StaleBuildNotice/showStaleBuildNotice.js';
import { errorMessage } from './utils/errors.js';
import { shouldInvalidate } from './utils/invalidation.js';
import { isStaleBuildError } from './utils/staleBuild.js';

/**
 * Studios and Games are the landing pages, so they stay in the entry bundle. Everything else
 * behind a click — the editors, Users, Audit and Environments — is its own chunk, fetched the
 * first time its route is reached. `AppLayout`'s `Suspense` (around the routed `<Outlet>`) is
 * what shows while one of these loads; the shell around it stays mounted throughout.
 */
const StudioEditorPage = lazy(() =>
    import('./pages/studios/StudioEditorPage.js').then((m) => ({ default: m.StudioEditorPage })),
);
const GameEditorPage = lazy(() =>
    import('./pages/games/GameEditorPage.js').then((m) => ({ default: m.GameEditorPage })),
);
const ConfigEditorPage = lazy(() =>
    import('./pages/configs/ConfigEditorPage.js').then((m) => ({ default: m.ConfigEditorPage })),
);
const EnvironmentsPage = lazy(() =>
    import('./pages/environments/EnvironmentsPage.js').then((m) => ({ default: m.EnvironmentsPage })),
);
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })));
const UsersPage = lazy(() => import('./pages/users/UsersPage.js').then((m) => ({ default: m.UsersPage })));

/**
 * The query and tRPC clients, built once per page load. The token is read per request
 * (`getToken`), never baked in: the ID token renews silently, and rebuilding the clients on
 * each renewal would throw away every cached query and in-flight mutation with it.
 */
const createClients = (getToken: () => string | null | undefined) => {
    const queryClient = new QueryClient({
        // Every failed query raises a red toast in one place, so no page hand-rolls
        // per-query error handling.
        queryCache: new QueryCache({
            onError: (error, query) => {
                // The server is another build than this page: say that, not the raw
                // "No procedure on path" — reloading is the fix. See `utils/staleBuild.ts`.
                if (isStaleBuildError(error)) return showStaleBuildNotice();
                // `meta: { quiet: true }` — the page renders this failure itself and a
                // toast would say it twice. See `utils/trpc.ts`.
                if (query.meta?.quiet) return;
                notifications.show({ message: errorMessage(error), color: 'red' });
            },
        }),
        // Every successful mutation invalidates every query family a write could plausibly
        // touch — everything except the four status/me probes (`utils/invalidation.ts`'s
        // `shouldInvalidate`), which are read on nearly every page and never change as a side
        // effect of a write elsewhere. `meta: { skipInvalidate: true }` (see `utils/trpc.ts`)
        // opts a mutation out entirely — for one run several times under a caller-owned
        // wrapper (Pull all's per-environment `pullNow`), whose own success invalidates once.
        mutationCache: new MutationCache({
            onSuccess: (_data, _variables, _onMutateResult, mutation) => {
                if (mutation.meta?.skipInvalidate) return;
                void queryClient.invalidateQueries({ predicate: (query) => shouldInvalidate(query.queryKey) });
            },
            // Every failed mutation raises a red toast here, unless it reports the failure itself
            // (`meta: { quiet: true }`, see `utils/trpc.ts`).
            onError: (error, _variables, _onMutateResult, mutation) => {
                if (isStaleBuildError(error)) return showStaleBuildNotice();
                if (mutation.meta?.quiet) return;
                notifications.show({ message: errorMessage(error), color: 'red' });
            },
        }),
        // 30s: most reads tolerate being half a minute stale, and it cuts refetch-on-mount
        // traffic across the matrix's paged queries. The four status/me probes override this
        // to `Infinity` at their own `queryOptions()` call sites — they never change from a
        // write this client itself makes.
        defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
    });

    const http = {
        url: TRPC_PATH,
        transformer: superjson,
        headers: () => {
            const token = getToken();
            return token ? { authorization: `Bearer ${token}` } : {};
        },
    };

    return {
        queryClient,
        // An actual client, not a bag of options: TRPCProvider calls `.query()` on what
        // it is given, so passing the link config produces a page that renders perfectly
        // and fails every request.
        trpcClient: createTRPCClient<AppRouter>({
            links: [
                // FormData (an asset upload) cannot go through superjson or a batch: send it
                // raw on its own request. The response is still superjson.
                splitLink({
                    condition: (op) => isNonJsonSerializable(op.input),
                    true: httpLink(http),
                    // The push dry run can carry a selection large enough to exceed a URL.
                    false: httpBatchLink({ ...http, methodOverride: 'POST' }),
                }),
            ],
        }),
    };
};

/**
 * The layout route's guard: signed in renders `children` (`AppLayout`, with the rest of the
 * tree under it); signed out bounces to `/login`, carrying the location it bounced FROM as
 * `state.from` so `LoginPage` can send the sign-in back to it instead of always landing on
 * Studios (see `auth/index.tsx`'s `onSigninCallback`).
 */
const RequireAuth = ({ children }: { children: ReactNode }) => {
    const auth = useClientAuth();
    const location = useLocation();
    if (!auth.isAuthenticated) {
        return <Navigate to={LOGIN_ROUTE} replace state={{ from: location } satisfies LoginRedirectState} />;
    }
    return <>{children}</>;
};

/**
 * A data router, not `<BrowserRouter>`: only a data router supports `useBlocker`, which is how a
 * dirty editor stops every way out — the sidebar, a breadcrumb, Back — not just its own buttons.
 */
const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route path={LOGIN_ROUTE} element={<LoginPage />} />
            <Route
                element={
                    <RequireAuth>
                        <AppLayout />
                    </RequireAuth>
                }
            >
                <Route index element={<Navigate to={STUDIOS_ROUTE} replace />} />
                {/* Keyed: sibling routes rendering the same component share one instance otherwise,
                    and a create turning into an edit relies on the editor remounting (its saved
                    banner, draft and form all read their start state on mount). */}
                <Route path={NEW_STUDIO_PATH} element={<StudioEditorPage key="new" />} />
                <Route path={STUDIO_EDIT_PATH} element={<StudioEditorPage key="edit" />} />
                <Route path={STUDIOS_ROUTE} element={<StudiosPage />} />
                <Route path={STUDIO_REDIRECT_PATH} element={<StudioRedirect />} />
                <Route path={NEW_GAME_PATH} element={<GameEditorPage key="new" />} />
                <Route path={GAME_EDIT_PATH} element={<GameEditorPage key="edit" />} />
                <Route path={GAME_ASSETS_REDIRECT_PATH} element={<GameAssetsRedirect />} />
                <Route path={GAMES_ROUTE} element={<GamesPage />} />
                <Route path={NEW_CONFIG_PATH} element={<ConfigEditorPage key="new" />} />
                <Route path={CONFIG_EDIT_PATH} element={<ConfigEditorPage key="edit" />} />
                <Route path={CONFIGS_ROUTE} element={<ConfigsPage />} />
                <Route path={ENVIRONMENTS_ROUTE} element={<EnvironmentsPage />} />
                <Route path={AUDIT_ROUTE} element={<AuditLogPage />} />
                <Route path={USERS_ROUTE} element={<UsersPage />} />
                <Route path="*" element={<Navigate to={STUDIOS_ROUTE} replace />} />
            </Route>
        </>,
    ),
);

export const App = () => {
    const auth = useClientAuth();
    const token = useRef(auth.token);
    token.current = auth.token;
    const [{ queryClient, trpcClient }] = useState(() => createClients(() => token.current));

    // Signed out (a token went away; `signOut` stays on the page): nothing the previous session
    // read may outlive it. Only on that transition — under the stub there is never a token, and
    // clearing on mount would drop the first page's in-flight queries and leave it loading.
    const previousToken = usePrevious(auth.token);
    useEffect(() => {
        if (previousToken && !auth.token) queryClient.clear();
    }, [auth.token, previousToken, queryClient]);

    if (auth.isLoading) {
        return <Loader message="Signing in…" />;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
                <ModalsProvider>
                    <Notifications position="top-right" />
                    <RouterProvider router={router} />
                </ModalsProvider>
            </TRPCProvider>
        </QueryClientProvider>
    );
};
